import { getEnvValue } from '../../Helper/env';
import { Icon, Text, useTheme } from '@ui-kitten/components';
import React from 'react';
import { View } from 'react-native';
import StyleSheet from 'react-native-media-query';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import MealPlanTypes from '../../Components/MealPlanTypes';
import constants from '../../Config/constants';
import { setMealPlanType } from '../../RTK/mealPlan';
import { selectMealPlanType } from '../../RTK/mealPlan/selectors';
import ThemeColor from '../../Theme/colors';
import ThemeStyle, { globalSpacing } from '../../Theme/styles';
import breakpoints from '../../Theme/styles/breakpoints';
import { SECTION_SUB_TITLE } from '../../Theme/typographyProps';

const { ORDER_TYPES } = constants;

const RAZZLE_BUILD_MODE = getEnvValue('RAZZLE_BUILD_MODE');
const isBranded = RAZZLE_BUILD_MODE === 'branded';

function OrderTypeNote({ mealPlanDayCount, storeAddress, type, goToLocation }) {
  const dispatch = useDispatch();
  const store_theme = useTheme();
  const mealPlanType = useSelector(selectMealPlanType);

  const isPickup = type.includes('pickup');
  const isMealPlan = type === ORDER_TYPES.MEAL_PLAN;

  const _onMealPlanTypeSelected = (data) => {
    dispatch(setMealPlanType(data.value));
  };

  const _renderTitle = (iconName, title) => (
    <View
      style={[ThemeStyle.flexDirectionRowCenter, ThemeStyle.spacingBottomSmall]}
    >
      <Icon
        name={iconName}
        fill={store_theme['icon_color'] || ThemeColor.green}
        style={styles.icon}
      />

      <Text category="p1">{title}</Text>
    </View>
  );

  if (isPickup) {
    return (
      <View
        style={
          isBranded
            ? ThemeStyle.spacingVerticalSmall
            : ThemeStyle.spacingVerticalMedium
        }
      >
        {_renderTitle('shopping-bag-outline', 'This is a Pickup order.')}

        <View style={[ThemeStyle.flexWrap, ThemeStyle.flexDirectionRowCenter]}>
          <Text {...SECTION_SUB_TITLE}>
            Please head over to{' '}
            <Text
              status="success"
              onPress={goToLocation}
              {...SECTION_SUB_TITLE}
            >
              {storeAddress}
            </Text>{' '}
            to pick up your order.
          </Text>
        </View>
      </View>
    );
  } else if (isMealPlan && mealPlanDayCount) {
    return (
      <View
        dataSet={{ media: ids.mealPlanNoteContainer }}
        style={[styles.mealPlanNoteContainer, ThemeStyle.spacingVerticalMedium]}
      >
        <View
          dataSet={{ media: ids.mealPlanText }}
          style={[styles.mealPlanText, ThemeStyle.flex1]}
        >
          {_renderTitle('calendar-outline', 'This is a Meal Plan order.')}
          <Text category="p2" style={ThemeStyle.flex1}>
            You've selected a {mealPlanDayCount} day meal plan. Please make sure
            to add at least one item per day and include one exclusive item
            between Wednesday and Friday.
          </Text>
        </View>
        <View
          dataSet={{ media: ids.mealPlanTypes }}
          style={styles.mealPlanTypes}
        >
          <MealPlanTypes
            value={mealPlanType}
            onChange={_onMealPlanTypeSelected}
            small
          />
        </View>
      </View>
    );
  }

  return null;
}

const { ids, styles } = StyleSheet.create({
  mealPlanNoteContainer: {
    [`@media ${breakpoints.sm}`]: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
    },
  },
  mealPlanText: {
    ...ThemeStyle.spacingBottomMedium,
    [`@media ${breakpoints.sm}`]: {
      ...ThemeStyle.spacingRightMedium,
    },
  },
  mealPlanTypes: {
    width: '100%',
    [`@media ${breakpoints.sm}`]: {
      maxWidth: 260,
    },
    [`@media ${breakpoints.xl}`]: {
      maxWidth: 320,
    },
  },
  icon: {
    width: 20,
    height: 20,
    marginRight: globalSpacing / 2,
  },
});

OrderTypeNote.propsTypes = {
  type: PropTypes.oneOf(Object.keys(ORDER_TYPES)),
  storeAddress: PropTypes.string.isRequired,
  goToLocation: PropTypes.func.isRequired,
};

export default OrderTypeNote;
